import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { usd } from "@/lib/currency";
import { PageHeader, Panel } from "@/components/dash";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { inputCls, btnPrimary, btnGhost } from "@/components/admin/AdminLayout";

const EMPTY = { title: "", category: "Special Reports", price: 2700, regular_price: 0, summary: "", description: "", published: true };

function ReportEditor({ report, onClose, onSaved }) {
  const [f, setF] = useState(report?.id ? { ...EMPTY, ...report } : EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.type === "number" ? Number(e.target.value) : e.target.value });
  const save = async () => {
    const body = { title: f.title, category: f.category, price: f.price, regular_price: f.regular_price, summary: f.summary, description: f.description, published: f.published };
    try {
      if (report?.id) await api.put(`/admin/reports/${report.id}`, body); else await api.post("/admin/reports", body);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const upload = async (kind, file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/admin/reports/${report.id}/file?kind=${kind}`, fd); toast.success(`${kind} file uploaded`); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-950 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{report?.id ? "Edit report" : "New report"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 text-sm">
          <input className={inputCls} placeholder="Title" value={f.title} onChange={set("title")} data-testid="report-edit-title" />
          <div className="grid grid-cols-3 gap-3">
            <input className={inputCls} placeholder="Category" value={f.category} onChange={set("category")} data-testid="report-edit-category" />
            <label className="text-xs text-slate-400">Price USD<input type="number" className={`${inputCls} w-full mt-1`} value={f.price} onChange={set("price")} data-testid="report-edit-price" /></label>
            <label className="text-xs text-slate-400">Regular USD<input type="number" className={`${inputCls} w-full mt-1`} value={f.regular_price} onChange={set("regular_price")} /></label>
          </div>
          <textarea className={`${inputCls} h-20 py-2`} placeholder="Short summary" value={f.summary} onChange={set("summary")} />
          <textarea className={`${inputCls} h-32 py-2 font-mono text-xs`} placeholder="Description (HTML allowed: table of contents, scope…)" value={f.description} onChange={set("description")} />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.published} onChange={set("published")} /> Published</label>
          {report?.id && (
            <div className="grid grid-cols-2 gap-3 border-t border-slate-800 pt-3">
              {["sample", "full"].map((k) => (
                <label key={k} className="text-xs text-slate-400 border border-dashed border-slate-700 rounded-md p-3 cursor-pointer hover:border-[#03AB9E]">
                  {k === "sample" ? "Sample PDF (public)" : "Full report (buyers only)"} {report[`has_${k}`] ? "✓ uploaded" : ""}
                  <input type="file" accept=".pdf,.docx,.xlsx" className="block mt-2 text-xs" data-testid={`report-upload-${k}`} onChange={(e) => e.target.files[0] && upload(k, e.target.files[0])} />
                </label>
              ))}
            </div>
          )}
          <button onClick={save} className={btnPrimary} data-testid="report-edit-save">Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminReports() {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [edit, setEdit] = useState(null);
  const [bulk, setBulk] = useState({ category: "", price: 2700, regular_price: 0 });
  const { data, refetch } = useQuery({ queryKey: ["admin-reports", q, page], queryFn: async () => (await api.get("/reports", { params: { q, page, size: 25, admin: true } })).data });
  const { data: cats = [] } = useQuery({ queryKey: ["report-cats"], queryFn: async () => (await api.get("/reports/categories")).data });
  const applyBulk = async () => { const r = (await api.post("/admin/reports/bulk-price", bulk)).data; toast.success(`${r.updated} reports updated`); refetch(); };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="CMS · Reports" title="Reports Catalog" desc="Add reports, set pricing, and upload sample and full PDFs.">
        <button className={btnPrimary} onClick={() => setEdit({})} data-testid="admin-report-new">+ New report</button>
      </PageHeader>
      <Panel title="Bulk pricing" sub="Apply a price to every report in a category (or all)">
        <div className="flex flex-wrap gap-3 items-end">
          <select className={inputCls} value={bulk.category} onChange={(e) => setBulk({ ...bulk, category: e.target.value })} data-testid="admin-bulk-category"><option value="">All categories</option>{cats.map((c) => <option key={c.name}>{c.name}</option>)}</select>
          <input type="number" className={`${inputCls} w-32`} value={bulk.price} onChange={(e) => setBulk({ ...bulk, price: Number(e.target.value) })} data-testid="admin-bulk-price" />
          <input type="number" className={`${inputCls} w-32`} value={bulk.regular_price} onChange={(e) => setBulk({ ...bulk, regular_price: Number(e.target.value) })} />
          <button className={btnGhost} onClick={applyBulk} data-testid="admin-bulk-apply">Apply</button>
        </div>
      </Panel>
      <Panel title={`${data?.total ?? "…"} reports`} right={<input className={`${inputCls} w-72`} placeholder="Search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} data-testid="admin-report-search" />}>
        <table className="w-full text-sm" data-testid="admin-reports-table">
          <thead className="bg-slate-900/80"><tr>{["Title", "Category", "Price", "Files", "Status", ""].map((h) => <th key={h} className="px-3 py-2 text-left font-mono text-[11px] uppercase text-slate-400">{h}</th>)}</tr></thead>
          <tbody>{data?.items.map((r) => (
            <tr key={r.id} className="border-t border-slate-800/70">
              <td className="px-3 py-2 max-w-md truncate">{r.title}</td><td className="px-3 py-2 text-slate-400">{r.category}</td>
              <td className="px-3 py-2 font-mono">{usd(r.price)}</td>
              <td className="px-3 py-2 text-xs">{r.has_sample ? <span className="text-[#03AB9E]">Sample</span> : <span className="text-slate-600">Sample</span>} · {r.has_full ? <span className="text-[#03AB9E]">Full</span> : <span className="text-slate-600">Full</span>}</td>
              <td className="px-3 py-2 text-xs">{r.published ? "Live" : "Hidden"}</td>
              <td className="px-3 py-2"><button className={btnGhost} onClick={() => setEdit(r)} data-testid={`admin-report-edit-${r.id}`}>Edit</button></td>
            </tr>))}</tbody>
        </table>
        <div className="flex justify-end gap-2 mt-3 text-xs font-mono">
          <button className={btnGhost} disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</button><span className="py-2">{page}</span>
          <button className={btnGhost} disabled={page * 25 >= (data?.total || 0)} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      </Panel>
      {edit && <ReportEditor report={edit} onClose={() => setEdit(null)} onSaved={() => { refetch(); setEdit(null); }} />}
    </div>
  );
}
